/**
 * UserTable - Table display of users with selection support
 */
export const getPlanBadge = (plan) => {
  const colors = {
    free: "bg-slate-600",
    starter: "bg-blue-600",
    pro: "bg-purple-600",
    org: "bg-teal-600",
  };
  return colors[plan] || "bg-slate-600";
};

export const getStatusBadge = (status) => {
  const colors = {
    active: "bg-green-600",
    trial: "bg-yellow-600",
    cancelled: "bg-red-600",
    past_due: "bg-orange-600",
  };
  return colors[status] || "bg-slate-600";
};

export default function UserTable({
  users,
  loading,
  selectedUserIds,
  onSelectAll,
  onSelectUser,
  onViewDetails,
}) {
  return (
    <div className="bg-card backdrop-blur-sm border border-border rounded-xl overflow-hidden">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No users found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedUserIds.length === users.length && users.length > 0}
                    onChange={onSelectAll}
                    className="w-4 h-4 rounded border-slate-600 bg-muted text-teal-500 focus:ring-primary focus:ring-offset-slate-900"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Credits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-muted/30 transition">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user.id)}
                      onChange={() => onSelectUser(user.id)}
                      className="w-4 h-4 rounded border-slate-600 bg-muted text-teal-500 focus:ring-primary focus:ring-offset-slate-900"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="text-foreground font-medium">{user.name}</p>
                      <p className="text-muted-foreground text-sm">{user.email}</p>
                      {user.email_verified && (
                        <span className="inline-flex items-center gap-1 text-green-400 text-xs mt-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Verified
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 ${getPlanBadge(user.plan_type)} text-foreground text-xs rounded-full capitalize`}>
                      {user.plan_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 ${getStatusBadge(user.subscription_status)} text-foreground text-xs rounded-full capitalize`}
                    >
                      {user.subscription_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-foreground">{user.credits_remaining || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-muted-foreground text-sm">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => onViewDetails(user.id)}
                      className="px-3 py-1 bg-primary hover:bg-primary/90 text-foreground text-sm rounded transition"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
